/**
 * Orchestrates the create-time team assignment flow shared by the
 * "New assessment" action (Ticket #49). The UI captures the team
 * picker selection at the same time the user fills in the rest of the
 * form, so on submit we POST /assessments and immediately PUT
 * /assessments/{id}/teams. This helper centralises the rules around
 * when the PUT should fire and how a partial failure is reported, so
 * the view layer stays thin and the behaviour can be exercised by
 * pure unit tests without spinning up React.
 *
 * Rules (mirroring the inline comment in AssessmentEditView):
 *   - The PUT is skipped when no teams were selected.
 *   - The PUT is skipped when the new assessment was created as a
 *     Draft (i.e. zero measures) — the server would 422, and the UI
 *     also disables the picker in that case.
 *   - If the PUT fails after a successful POST, the assessment still
 *     exists on the server. The caller is expected to surface the
 *     ``teamAssignmentError`` to the admin and still navigate them
 *     into edit mode for the newly created assessment so they can
 *     retry from there.
 *
 * Any error thrown by ``createAssessment`` itself propagates — the
 * caller's existing onError handling for the POST mutation owns that
 * surface.
 */
import type { AssessmentDetail, AssessmentInput } from "./api";

export type CreateAssessmentWithTeamsDeps = {
  createAssessment: (body: AssessmentInput) => Promise<AssessmentDetail>;
  setAssessmentTeams: (
    assessmentId: string,
    teamIds: string[],
  ) => Promise<unknown>;
};

export type CreateAssessmentWithTeamsResult = {
  created: AssessmentDetail;
  teamPutCalled: boolean;
  teamAssignmentError: string | null;
};

export async function createAssessmentWithTeams(
  body: AssessmentInput,
  teamIds: string[],
  deps: CreateAssessmentWithTeamsDeps,
): Promise<CreateAssessmentWithTeamsResult> {
  const created = await deps.createAssessment(body);
  if (teamIds.length === 0 || created.status !== "saved") {
    return { created, teamPutCalled: false, teamAssignmentError: null };
  }
  try {
    await deps.setAssessmentTeams(created.id, teamIds);
    return { created, teamPutCalled: true, teamAssignmentError: null };
  } catch (e) {
    const teamAssignmentError =
      (e as { message?: string })?.message || "Failed to assign teams";
    return { created, teamPutCalled: true, teamAssignmentError };
  }
}
